/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { Button, Form, Icon, Modal } from '@mairistem/composants';
import { EtatCivil, TypeComponent } from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { ChampAdresse, ChampLieu } from '@/components/Common';
import { buildIndividuPutPayload, parseIndividuFiche } from '@/utils';
import { validationInformationsNaissance } from '@/utils/validations';

interface ModalDefuntProps {
  /** Endpoint générique des individus d'acte (`PUT {urlIndividus}/{id}`). */
  urlIndividus?: string;
  open: boolean;
  /** L'individu défunt, tel que lu dans le dossier. */
  individu?: any;
  /**
   * Titre de la modale (défaut « Modifier le défunt »).
   *
   * Le contenu ne dépend pas du rôle : état civil, lieu de naissance et
   * domicile, tous sur `ec_acte_individu`. Les conjoints d'un mariage s'éditent
   * donc avec la même modale, sous un autre titre — cf. `ModalConjoint`.
   */
  titre?: string;
  onClose: () => void;
  onSaved: (individu: any) => void;
}

/**
 * Sections « INFORMATIONS DE NAISSANCE » et « DOMICILIATION » de l'onglet du
 * défunt — le premier des deux groupes.
 *
 * Tout ce qu'elle édite est sur `ec_acte_individu` : un seul
 * `PUT /acte-individus/{id}`. C'est le découpage en deux crayons qui l'a permis,
 * la profession et l'état matrimonial étant passés dans `ModalDefuntDivers`.
 *
 * ⚠️ Le LIEU du décès n'est pas ici, bien qu'il soit aussi sur l'individu : la
 * maquette le place dans le bloc du décès, avec sa date.
 */
const CHAMPS_EDITABLES = [
  'nomFamille',
  'nomUsage',
  'prenom',
  'prenomsComplementaires',
  'sexe',
  'jourNaissance',
  'moisNaissance',
  'anneeNaissance',
  'adresseNaissance',
  'adresseDomicile',
];

const ModalDefunt = (props: ModalDefuntProps) => {
  const {
    open,
    urlIndividus,
    individu,
    titre = 'Modifier le défunt',
    onClose,
    onSaved,
  } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData(_.pick(parseIndividuFiche(individu), CHAMPS_EDITABLES));
    setAttempted(false);
  }, [open, individu]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const errors = React.useMemo(
    () => validationInformationsNaissance(data),
    [data],
  );

  const displayedErrors = attempted ? errors : {};

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    // Le dossier de décès n'expose pas d'individu dans son PUT : l'endpoint
    // générique est le seul chemin, il n'y a donc pas de repli sur `service`.
    if (!urlIndividus || _.isNil(individu?.identifiant)) {
      return;
    }

    const corps = buildIndividuPutPayload(individu, data);

    setSaving(true);
    request
      .put(`${urlIndividus}/${individu.identifiant}`, corps)
      .then(() => {
        onSaved({ ...individu, ...corps });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de l'individu",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [errors, urlIndividus, individu, data, onSaved, onClose]);

  /*
   * `size="large"` parce que cette modale contient un `ChampAdresse` : en
   * `small`, sa ligne de voirie écrase ses champs.
   *
   * ⚠️ Le critère est la présence d'un `ChampAdresse`, PAS le type d'acte : une
   * modale qui n'a qu'un `ChampLieu` reste en `small`.
   */
  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header content={titre} />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <EtatCivil
            title="État civil"
            typeComponent={TypeComponent.NAISSANCE}
            individu={data}
            errors={displayedErrors}
            onChange={handleChange}
          />
          <ChampLieu
            data={data}
            errors={{ adresseNaissance: displayedErrors.adresseNaissance }}
            onChange={handleChange}
          />
          <ChampAdresse
            data={data}
            onChange={handleChange}
            name="adresseDomicile"
            title="Adresse de domicile"
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="defunt-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalDefunt;
